import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const FileSelectionDialog = ({ open, onClose, businessId, onFileSelect }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filesData, setFilesData] = useState(null);

  useEffect(() => {
    if (open) {
      // Only suppliers can access this dialog
      fetchSupplierFiles();
    } else {
      // Reset state when dialog closes
      setFilesData(null);
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, businessId]);

  const fetchSupplierFiles = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get('/api/supplier/my-files', {
        params: businessId ? { businessId: String(businessId) } : undefined
      });
      setFilesData(response.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (file) => {
    if (onFileSelect) {
      onFileSelect(file);
    }
    onClose();
  };

  const handleNavigateToEditProfile = () => {
    navigate('/supplier/edit-profile');
    onClose();
  };

  const getFileIcon = (fileUrl) => {
    const extension = fileUrl.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <PdfIcon color="error" />;
    }
    return <FileIcon color="primary" />;
  };

  // Suppliers see all their business files
  const availableFiles = filesData?.files || [];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">Select a file to share with the couple</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : filesData ? (
          <>
            {availableFiles.length > 0 ? (
              <Box>
                <List>
                  {availableFiles.map((file, index) => (
                    <React.Fragment key={file._id}>
                      <ListItem 
                        disablePadding
                        onClick={() => handleFileSelect(file)}
                        sx={{
                          cursor: 'pointer',
                          py: 1.5,
                          px: 0,
                          borderRadius: 1,
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getFileIcon(file.fileUrl)}
                        </ListItemIcon>
                        <ListItemText 
                          primary={file.name}
                          secondary={`Uploaded ${new Date(file.uploadedAt).toLocaleDateString()}`}
                          sx={{ 
                            '& .MuiListItemText-primary': {
                              fontSize: '0.95rem'
                            },
                            '& .MuiListItemText-secondary': {
                              fontSize: '0.85rem'
                            }
                          }}
                        />
                      </ListItem>
                      {index < availableFiles.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No files available. Upload files to share with couples.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNavigateToEditProfile}
                  sx={{ mt: 2 }}
                >
                  Go to Edit Profile
                </Button>
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={onClose}
          variant="contained"
          color="primary"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileSelectionDialog;
